import {
	autoPlacement,
	FloatingPortal,
	offset,
	size,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
} from '@floating-ui/react'
import { ReactNode, useState } from 'react'
import { Z_INDEX_POPUP } from '../../constants'

export interface GenericPopupInputProps {
	children: ReactNode
	trigger: ReactNode
	triggerWrapperClassName?: string
	useReferenceWidth?: boolean // if it should match the parent width; sensible for selects
}

export function GenericPopupInput(
	{ children, trigger, triggerWrapperClassName, useReferenceWidth = true }: GenericPopupInputProps,
) {
	const [isOpen, setIsOpen] = useState(false)

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [
			autoPlacement(),
			offset(8),
			size({
				apply({ rects, elements }) {
					if (useReferenceWidth) {
						Object.assign(elements.floating.style, {
							minWidth: `${rects.reference.width}px`,
						})
					}
				},
			}),
		],
	})

	const click = useClick(context)
	const dismiss = useDismiss(context)

	const { getReferenceProps, getFloatingProps } = useInteractions([
		dismiss,
		click,
	])

	return (
		<>
			<div
				ref={refs.setReference}
				{...getReferenceProps()}
				data-is="generic-popup-input-trigger"
				className={triggerWrapperClassName}
			>
				{trigger}
			</div>
			{isOpen && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={{ zIndex: Z_INDEX_POPUP, ...floatingStyles }}
						{...getFloatingProps()}
					>
						<div className="bg-gradient-to-b from-white to-grey-50 rounded-md p-1 shadow-md ring ring-grey-200/30">
							<div className="bg-white rounded-sm p-4 ring ring-grey-200/30">
								{children}
							</div>
						</div>
					</div>
				</FloatingPortal>
			)}
		</>
	)
}
